import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { Layer } from "../../types/layer";
import LayerControlItem from "./LayerControlItem";
import FilterModal, { Filter } from "./FilterModal";
import ImportPropertiesModal from "./ImportPropertiesModal";
import { getAttributesFromLayer } from "../../utils/layerFilters";
import { useAuth } from "../../contexts/AuthContext";
import { isViewOnly } from "../../utils/permissions";
import { useProject } from "../../api/project";
import { useImportProperties } from "../../api/properties";

interface PropertyBoundaryControlProps {
  layer: Layer | null;
  filters: Filter[];
  onFiltersChange: (filters: Filter[]) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const PropertyBoundaryControl: React.FC<PropertyBoundaryControlProps> = ({ layer, filters, onFiltersChange, expanded, onExpandedChange }) => {
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const { project } = useProject();
  const existingCount = project?.data?.properties?.length ?? 0;
  const importProperties = useImportProperties(projectId);

  const attributes = useMemo(() => {
    return layer?.rawSource ? getAttributesFromLayer(layer.rawSource) : [];
  }, [layer?.rawSource]);

  if (!layer) return null;

  const handleToggleVisibility = () => {
    layer.toolbarButton?.props?.onClick?.();
  };

  // Mirrors the treatments/assets "Upload File" flow: pick a filestore file,
  // the server parses it and creates the Property rows. The layer re-reads
  // project.data.properties once the project query refetches.
  const importContent = viewOnly ? null : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setImportOpen(true)}
        sx={{
          color: "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: "transparent",
          fontSize: "0.75rem",
          "&:hover": { backgroundColor: "rgba(0, 212, 173, 0.1)" },
        }}
      >
        Add from file
      </Button>
      <Typography variant="caption" sx={{ color: "#94a3b8" }}>
        {existingCount > 0
          ? `${existingCount} propert${existingCount === 1 ? "y" : "ies"} on this project`
          : "No property boundaries yet"}
      </Typography>
    </Box>
  );

  const handleFiltersChange = (newFilters: Filter[]) => {
    onFiltersChange(newFilters);
  };

  return (
    <>
      <ImportPropertiesModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={(payload) => importProperties.mutateAsync(payload)}
        isPending={importProperties.isPending}
        existingCount={existingCount}
      />
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Properties"
        attributes={attributes}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />
      
      <LayerControlItem
        title="Properties"
        icon="location_on"
        visible={layer.active ?? false}
        onToggleVisibility={handleToggleVisibility}
        enabled={!!layer.rawSource}
        filterCount={filters.length}
        onFilterClick={() => setFilterModalOpen(true)}
        filterDisabled={attributes.length === 0}
        drawingContent={importContent}
        drawingContentLabel="Property boundaries"
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        keepOpen={importOpen}
      />
    </>
  );
};

export default PropertyBoundaryControl;
